import { PRODUCTS } from "codes";
import {
  ACCOUNT_OTHER_DOCUMENT_TYPE,
  OTHER_DOCUMENTS_BOX_LABEL,
  isSupplementaryAccountDocumentType,
  resolveSupplementaryDocumentSlotId,
  ensureTransportProofSubItems,
  enrichTransportProofChildSlot,
  slotRequiresYear,
  toAdditionalDocumentTypeCode,
  transportProofChildRequiresYear,
} from "helpers/additionalDocumentHelpers";
import {
  AUDITED_FINANCIALS_DOC_TYPE_ID,
  FINANCIAL_REPORT_DOC_TYPE_ID,
  FINANCIAL_STATEMENT_DOC_TYPE_ID,
  MANAGEMENT_FINANCIALS_DOC_TYPE_ID,
  normalizeDocumentLabel,
} from "helpers/financialDocumentConstants";
import { remapUploadedIdDocumentSlot } from "helpers/documentSlotMatching";
import toCamelCase from "helpers/camelCase";
import type { CompanyDoc, RequiredDoc } from "types";

export {
  AUDITED_FINANCIALS_DOC_TYPE_ID,
  FINANCIAL_STATEMENT_DOC_TYPE_ID,
  MANAGEMENT_FINANCIALS_DOC_TYPE_ID,
};

export const FINANCIAL_STATEMENT_DISPLAY_NAME = "Financial Statement";
export const AUDITED_FINANCIALS_DISPLAY_NAME = "Audited financials";
export const AUDITED_FINANCIALS_DOCUMENTS_TAB_DISPLAY_NAME =
  "Audited financials";
export const MANAGEMENT_ACCOUNTS_DISPLAY_NAME = "Management accounts";
export const MANAGEMENT_ACCOUNTS_DOCUMENTS_TAB_DISPLAY_NAME =
  "Management accounts";

const FINANCIAL_UPLOAD_DOC_TYPE_IDS = new Set<string>([
  FINANCIAL_REPORT_DOC_TYPE_ID,
  FINANCIAL_STATEMENT_DOC_TYPE_ID,
  AUDITED_FINANCIALS_DOC_TYPE_ID,
  MANAGEMENT_FINANCIALS_DOC_TYPE_ID,
]);

export const normalizeDocName = normalizeDocumentLabel;

export const isFinancialStatementDoc = (doc: RequiredDoc) =>
  doc.id === FINANCIAL_STATEMENT_DOC_TYPE_ID ||
  normalizeDocName(doc.display_name) === "financialstatement";

export const isStandaloneFinancialChildId = (docId?: string | null): boolean =>
  docId === AUDITED_FINANCIALS_DOC_TYPE_ID ||
  docId === MANAGEMENT_FINANCIALS_DOC_TYPE_ID;

export const isFinancialSplitChildDoc = (doc: RequiredDoc) => {
  if (isStandaloneFinancialChildId(doc.id)) {
    return true;
  }

  const normalizedName = normalizeDocName(doc.display_name);
  return (
    normalizedName === "auditedfinancials" ||
    normalizedName === "managementfinancials"
  );
};

/** True when this doc uses the split Audited + Management boxes (not the single Factoring box). */
export const isFinancialParentSplitDoc = (doc: RequiredDoc) =>
  Boolean(
    doc.items?.some((sub) => sub.id === AUDITED_FINANCIALS_DOC_TYPE_ID) &&
      doc.items?.some((sub) => sub.id === MANAGEMENT_FINANCIALS_DOC_TYPE_ID),
  );

/**
 * SF / required-doc list sometimes adds a second row (e.g. "Management Financial Statement")
 * that duplicates uploads already shown under split "Financial documents" → Management Financials.
 */
export const isDuplicateManagementFinancialStatementSlot = (
  doc: RequiredDoc,
  hasFinancialSplitParent: boolean,
): boolean => {
  if (!hasFinancialSplitParent) return false;
  if (isFinancialParentSplitDoc(doc)) return false;

  const n = normalizeDocName(doc.display_name);
  const id = (doc.id ?? "").toLowerCase().replace(/_/g, "-");
  return (
    n === "managementfinancialstatement" ||
    n === "managementfinancialstatements" ||
    id === "management-financial-statement" ||
    id === "management-financial-statements"
  );
};

/** Client Factoring: one simple "Financial Statement" upload. Reverse Factoring (and others): audited + management split UX. */
export const isFactoringAccountProduct = (account_product?: string | null) => {
  const p = (account_product ?? "").toLowerCase().trim().replace(/\s+/g, "_");
  return p === PRODUCTS.factoring;
};

export const isBuyerCompanyType = (companyType?: string | null) =>
  (companyType ?? "").toLowerCase().trim() === "buyer";

export const isSellerCompanyType = (companyType?: string | null) =>
  (companyType ?? "").toLowerCase().trim() === "seller";

/** Standard Factoring buyer slots that must be uploaded per year (2 sets). */
export const FACTORING_BUYER_YEAR_DOCUMENT_SLOT_IDS = new Set([
  "invoices",
  "repayment-slip",
  "purchase-order-sales-contract",
]);

export const notesSlotRequiresYear = (
  doc: Pick<
    RequiredDoc,
    "id" | "requires_year" | "yearly_set_count" | "required_upload_count"
  >,
  account_product?: string | null,
  companyType?: string | null,
): boolean => {
  if (slotRequiresYear(doc)) {
    return true;
  }

  if (transportProofChildRequiresYear(doc.id)) {
    return true;
  }

  return (
    isFactoringAccountProduct(account_product) &&
    isBuyerCompanyType(companyType) &&
    Boolean(doc.id && FACTORING_BUYER_YEAR_DOCUMENT_SLOT_IDS.has(doc.id))
  );
};

export const isReverseFactoringAccountProduct = (
  account_product?: string | null,
) => {
  const p = (account_product ?? "").toLowerCase().trim().replace(/\s+/g, "_");
  return p === PRODUCTS.reverse_factoring;
};

/**
 * Keep Financial Statement as one upload slot when:
 * - account product is factoring, OR
 * - reverse-factoring + seller/trade-partner profile (client side).
 */
export const shouldUseSimpleFinancialUpload = (
  account_product?: string | null,
  companyType?: string | null,
) =>
  isFactoringAccountProduct(account_product) ||
  (isReverseFactoringAccountProduct(account_product) &&
    isSellerCompanyType(companyType));

export const ensureFinancialStatementSubItems = (
  doc: RequiredDoc,
): RequiredDoc => {
  if (!isFinancialStatementDoc(doc)) return doc;

  const existingItems = doc.items ?? [];

  const auditedExisting = existingItems.find(
    (item) => item.id === AUDITED_FINANCIALS_DOC_TYPE_ID,
  );
  const managementExisting = existingItems.find(
    (item) => item.id === MANAGEMENT_FINANCIALS_DOC_TYPE_ID,
  );

  const yearlySetCount =
    doc.yearly_set_count && doc.yearly_set_count > 1 ? doc.yearly_set_count : 4;

  const auditedItem =
    auditedExisting ??
    ({
      ...doc,
      id: AUDITED_FINANCIALS_DOC_TYPE_ID,
    } as RequiredDoc);

  const managementItem =
    managementExisting ??
    ({
      ...doc,
      id: MANAGEMENT_FINANCIALS_DOC_TYPE_ID,
    } as RequiredDoc);

  return {
    ...doc,
    display_name: FINANCIAL_STATEMENT_DISPLAY_NAME,
    yearly_set_count: yearlySetCount,
    items: [
      {
        ...auditedItem,
        display_name: AUDITED_FINANCIALS_DOCUMENTS_TAB_DISPLAY_NAME,
        is_mandatory: true,
        items: [],
        yearly_set_count: auditedItem.yearly_set_count ?? 2,
        required_upload_count: auditedItem.required_upload_count ?? 2,
      },
      {
        ...managementItem,
        display_name: MANAGEMENT_ACCOUNTS_DOCUMENTS_TAB_DISPLAY_NAME,
        is_mandatory: false,
        items: [],
        yearly_set_count: managementItem.yearly_set_count ?? 2,
        required_upload_count: managementItem.required_upload_count ?? 2,
      },
    ],
  };
};

export const simplifyFinancialDocForFactoring = (
  doc: RequiredDoc,
): RequiredDoc => {
  if (!isFinancialStatementDoc(doc) && !isFinancialSplitChildDoc(doc))
    return doc;

  return {
    ...doc,
    id: FINANCIAL_STATEMENT_DOC_TYPE_ID,
    display_name: FINANCIAL_STATEMENT_DISPLAY_NAME,
    items: [],
    yearly_set_count: 1,
    required_upload_count: 1,
    requires_year: false,
  };
};

export const transformFinancialRequiredDoc = (
  doc: RequiredDoc,
  account_product?: string | null,
  companyType?: string | null,
): RequiredDoc =>
  ensureTransportProofSubItems(
    shouldUseSimpleFinancialUpload(account_product, companyType)
      ? simplifyFinancialDocForFactoring(doc)
      : ensureFinancialStatementSubItems(doc),
  );

export const getNotesFinancialParentDisplayName = (
  _account_product?: string | null,
  _companyType?: string | null,
) => FINANCIAL_STATEMENT_DISPLAY_NAME;

export const applyNotesFinancialChildLabels = (
  doc: RequiredDoc,
  account_product?: string | null,
  companyType?: string | null,
): RequiredDoc => {
  if (!doc.items?.length) {
    return doc;
  }

  return {
    ...doc,
    display_name: getNotesFinancialParentDisplayName(
      account_product,
      companyType,
    ),
    items: doc.items.map((item) => {
      if (item.id === AUDITED_FINANCIALS_DOC_TYPE_ID) {
        return {
          ...item,
          display_name: AUDITED_FINANCIALS_DISPLAY_NAME,
          is_mandatory: true,
        };
      }
      if (item.id === MANAGEMENT_FINANCIALS_DOC_TYPE_ID) {
        return {
          ...item,
          display_name: MANAGEMENT_ACCOUNTS_DISPLAY_NAME,
          is_mandatory: false,
        };
      }
      return item;
    }),
  };
};

const PROFILE_DOCUMENT_CATEGORIES = new Set(["buyer", "seller", "supplier"]);

export const resolveDocumentSlotId = (
  doc: Pick<CompanyDoc, "document_type" | "required_document_id" | "all_data">,
): string => resolveSupplementaryDocumentSlotId(doc) || doc.document_type;

export const resolveDocumentCategoryLabel = (
  doc: CompanyDoc,
  categoryMap: Map<string, string>,
): string => {
  if (doc.document_type === ACCOUNT_OTHER_DOCUMENT_TYPE) {
    return OTHER_DOCUMENTS_BOX_LABEL;
  }

  const slotId = resolveDocumentSlotId(doc);
  const fromMap =
    categoryMap.get(doc.document_type) ??
    (slotId ? categoryMap.get(slotId) : undefined);
  if (fromMap) {
    return fromMap;
  }

  const typeName = (doc.document_type_name ?? "").trim();
  if (typeName && !PROFILE_DOCUMENT_CATEGORIES.has(typeName.toLowerCase())) {
    return typeName;
  }

  return slotId || doc.document_type || "Other";
};

export const isFinancialUploadDocumentType = (documentType?: string | null) =>
  Boolean(
    documentType &&
      FINANCIAL_UPLOAD_DOC_TYPE_IDS.has(
        documentType.toLowerCase().trim().replace(/_/g, "-"),
      ),
  );

export const remapUploadedFinancialDocumentType = (
  doc: Pick<CompanyDoc, "document_type" | "document_type_name">,
  options: {
    useSimpleFinancialUpload: boolean;
    hasFinancialSplitParent: boolean;
    currentDocIds: Set<string>;
  },
): string => {
  const documentType = doc.document_type;
  if (!isFinancialUploadDocumentType(documentType)) {
    return documentType;
  }

  const { useSimpleFinancialUpload, hasFinancialSplitParent, currentDocIds } =
    options;
  const typeName = (doc.document_type_name ?? "").toLowerCase();
  const isManagementFinancialType = typeName.includes("management");

  if (
    useSimpleFinancialUpload &&
    currentDocIds.has(FINANCIAL_STATEMENT_DOC_TYPE_ID)
  ) {
    return FINANCIAL_STATEMENT_DOC_TYPE_ID;
  }

  if (!hasFinancialSplitParent) {
    return documentType;
  }

  if (
    documentType === FINANCIAL_STATEMENT_DOC_TYPE_ID ||
    documentType === FINANCIAL_REPORT_DOC_TYPE_ID
  ) {
    const targetType = isManagementFinancialType
      ? MANAGEMENT_FINANCIALS_DOC_TYPE_ID
      : AUDITED_FINANCIALS_DOC_TYPE_ID;
    if (currentDocIds.has(targetType)) {
      return targetType;
    }
  }

  return documentType;
};

export const transformAndMergeRequiredDocs = (
  sourceDocs: RequiredDoc[],
  account_product?: string | null,
  companyType?: string | null,
): RequiredDoc[] => {
  const transformedDocs = sourceDocs.map((doc) =>
    transformFinancialRequiredDoc(doc, account_product, companyType),
  );

  const mergedById = new Map<string, RequiredDoc>();

  transformedDocs.forEach((doc) => {
    const existing = mergedById.get(doc.id);
    if (!existing) {
      mergedById.set(doc.id, doc);
      return;
    }

    const mergedItems =
      (existing.items?.length ? existing.items : doc.items) ?? [];

    mergedById.set(doc.id, {
      ...existing,
      is_mandatory: existing.is_mandatory || doc.is_mandatory,
      yearly_set_count: Math.max(
        existing.yearly_set_count || 1,
        doc.yearly_set_count || 1,
      ),
      required_upload_count: Math.max(
        existing.required_upload_count || 1,
        doc.required_upload_count || 1,
      ),
      requires_year: Boolean(existing.requires_year || doc.requires_year),
      items: mergedItems,
    });
  });

  return Array.from(mergedById.values());
};

export type FinancialUploadSections = {
  financialParentDoc?: RequiredDoc;
  hasFinancialSplitParent: boolean;
  docsVisibleInList: RequiredDoc[];
  financialChildDocDictionary: any[];
};

export type UploadControllerDictionaryOptions = {
  includeIsRequired?: boolean;
  includeSubItemYearlySetCount?: boolean;
};

export const filterDocsVisibleInUploadList = (
  docsWithFinancialSubItems: RequiredDoc[],
  options?: { excludeFinancialParent?: boolean },
): Pick<
  FinancialUploadSections,
  "financialParentDoc" | "hasFinancialSplitParent" | "docsVisibleInList"
> => {
  const financialParentDoc = docsWithFinancialSubItems.find(
    isFinancialParentSplitDoc,
  );
  const hasFinancialSplitParent = Boolean(financialParentDoc);
  const excludeFinancialParent = options?.excludeFinancialParent !== false;
  const docsVisibleInList = docsWithFinancialSubItems.filter(
    (doc) =>
      (excludeFinancialParent ? !isFinancialParentSplitDoc(doc) : true) &&
      !(hasFinancialSplitParent && isStandaloneFinancialChildId(doc.id)) &&
      !isDuplicateManagementFinancialStatementSlot(
        doc,
        hasFinancialSplitParent,
      ),
  );

  return { financialParentDoc, hasFinancialSplitParent, docsVisibleInList };
};

export const toFinancialChildDocDictionary = (
  financialParentDoc?: RequiredDoc,
  options?: { includeUploadCounts?: boolean },
): any[] => {
  if (!financialParentDoc) {
    return [];
  }

  return (financialParentDoc.items ?? []).map((sub) => ({
    ...sub,
    name: toCamelCase(sub.display_name),
    label: sub.display_name,
    value: sub.display_name,
    isRequired: financialParentDoc.is_mandatory !== false,
    ...(options?.includeUploadCounts
      ? { count_unique_uploaded: sub.count_unique_uploaded ?? 0 }
      : {}),
    items: [],
  })) as any[];
};

export const buildFinancialUploadSections = (
  docsWithFinancialSubItems: RequiredDoc[],
  options?: { includeUploadCounts?: boolean },
): FinancialUploadSections => {
  const { financialParentDoc, hasFinancialSplitParent, docsVisibleInList } =
    filterDocsVisibleInUploadList(docsWithFinancialSubItems);

  return {
    financialParentDoc,
    hasFinancialSplitParent,
    docsVisibleInList,
    financialChildDocDictionary: toFinancialChildDocDictionary(
      financialParentDoc,
      options,
    ),
  };
};

export const remapAccountDocsForFinancialDisplay = (
  accountDocs: CompanyDoc[] | undefined,
  docsWithFinancialSubItems: RequiredDoc[],
  account_product?: string | null,
): CompanyDoc[] | undefined => {
  if (!accountDocs) {
    return accountDocs;
  }

  const { hasFinancialSplitParent } = filterDocsVisibleInUploadList(
    docsWithFinancialSubItems,
  );
  const isFactoringSimpleFinancial = isFactoringAccountProduct(account_product);

  const duplicateIds = new Set(
    docsWithFinancialSubItems
      .filter((doc) =>
        isDuplicateManagementFinancialStatementSlot(
          doc,
          hasFinancialSplitParent,
        ),
      )
      .map((doc) => doc.id),
  );

  let next = accountDocs;
  if (isFactoringSimpleFinancial) {
    next = accountDocs.map((doc) => {
      if (
        doc.document_type === AUDITED_FINANCIALS_DOC_TYPE_ID ||
        doc.document_type === MANAGEMENT_FINANCIALS_DOC_TYPE_ID
      ) {
        return { ...doc, document_type: FINANCIAL_STATEMENT_DOC_TYPE_ID };
      }
      return doc;
    });
  }

  if (hasFinancialSplitParent && duplicateIds.size > 0) {
    next = next.map((doc) =>
      duplicateIds.has(doc.document_type)
        ? { ...doc, document_type: MANAGEMENT_FINANCIALS_DOC_TYPE_ID }
        : doc,
    );
  }

  return next;
};

export type RemapUploadedAccountDocumentsInput = {
  uploadedDocs: CompanyDoc[] | undefined;
  docsWithFinancialSubItems: RequiredDoc[];
  allDocumentsForRemapping: RequiredDoc[];
  account_product?: string | null;
  companyType?: string | null;
  /** Docs used for fallback error-path filtering when remapping throws. */
  fallbackCategoryDocs?: RequiredDoc[];
};

export type RemapUploadedAccountDocumentsResult = {
  filteredRemappedData: CompanyDoc[];
  allRemappedUploadedDocs: CompanyDoc[];
};

/**
 * Maps uploaded account documents onto required-doc slot ids (financial split,
 * duplicate management rows, director/signatory IDs, name-based legacy UUIDs).
 */
export const remapUploadedAccountDocuments = ({
  uploadedDocs,
  docsWithFinancialSubItems,
  allDocumentsForRemapping,
  account_product,
  companyType,
  fallbackCategoryDocs,
}: RemapUploadedAccountDocumentsInput): RemapUploadedAccountDocumentsResult => {
  const useSimpleFinancialUpload = shouldUseSimpleFinancialUpload(
    account_product,
    companyType,
  );
  const fallbackDocs = fallbackCategoryDocs ?? docsWithFinancialSubItems;

  try {
    const financialParentDoc = docsWithFinancialSubItems.find(
      isFinancialParentSplitDoc,
    );
    const hasFinancialSplitParent = Boolean(financialParentDoc);

    const duplicateMgmtStatementDocIds = new Set(
      docsWithFinancialSubItems
        .filter((doc) =>
          isDuplicateManagementFinancialStatementSlot(
            doc,
            hasFinancialSplitParent,
          ),
        )
        .map((doc) => doc.id),
    );

    const allDocIds = new Set<string>();
    const allDocByName = new Map<string, string>();

    allDocumentsForRemapping.forEach((doc) => {
      allDocIds.add(doc.id);
      allDocByName.set(normalizeDocName(doc.display_name), doc.id);
      doc.items?.forEach((subItem) => {
        allDocIds.add(subItem.id);
        allDocByName.set(normalizeDocName(subItem.display_name), subItem.id);
      });
    });

    const currentDocIds = new Set<string>();
    docsWithFinancialSubItems.forEach((doc) => {
      if (hasFinancialSplitParent && isStandaloneFinancialChildId(doc.id)) {
        return;
      }
      if (
        isDuplicateManagementFinancialStatementSlot(
          doc,
          hasFinancialSplitParent,
        )
      ) {
        return;
      }
      currentDocIds.add(doc.id);
      doc.items?.forEach((subItem) => {
        currentDocIds.add(subItem.id);
      });
    });

    const resolveDuplicateMgmtStatementType = (typeId: string) => {
      if (
        hasFinancialSplitParent &&
        duplicateMgmtStatementDocIds.has(typeId) &&
        currentDocIds.has(MANAGEMENT_FINANCIALS_DOC_TYPE_ID)
      ) {
        return MANAGEMENT_FINANCIALS_DOC_TYPE_ID;
      }
      return typeId;
    };

    const remappedInitialData =
      uploadedDocs?.map((doc) => {
        try {
          if (isSupplementaryAccountDocumentType(doc.document_type)) {
            return doc;
          }

          const remappedFinancialType = remapUploadedFinancialDocumentType(
            doc,
            {
              useSimpleFinancialUpload,
              hasFinancialSplitParent,
              currentDocIds,
            },
          );
          if (remappedFinancialType !== doc.document_type) {
            return { ...doc, document_type: remappedFinancialType };
          }

          const resolvedFromDuplicateSlot = resolveDuplicateMgmtStatementType(
            doc.document_type,
          );
          if (resolvedFromDuplicateSlot !== doc.document_type) {
            return { ...doc, document_type: resolvedFromDuplicateSlot };
          }

          const remappedIdSlot = remapUploadedIdDocumentSlot(
            { ...doc, document_type: resolvedFromDuplicateSlot },
            currentDocIds,
          );
          if (remappedIdSlot.document_type !== doc.document_type) {
            return remappedIdSlot;
          }

          if (allDocIds.has(doc.document_type)) {
            if (currentDocIds.has(doc.document_type)) {
              return doc;
            }
          }

          const docTypeName = doc.document_type_name;
          if (docTypeName) {
            const matchedId = allDocByName.get(normalizeDocName(docTypeName));
            if (matchedId) {
              const resolvedId = resolveDuplicateMgmtStatementType(matchedId);
              return { ...doc, document_type: resolvedId };
            }
          }

          if (doc.document_type === FINANCIAL_STATEMENT_DOC_TYPE_ID) {
            const normalizedDocTypeName = normalizeDocName(docTypeName);
            const mappedFinancialType = normalizedDocTypeName.includes(
              "management",
            )
              ? MANAGEMENT_FINANCIALS_DOC_TYPE_ID
              : AUDITED_FINANCIALS_DOC_TYPE_ID;
            if (currentDocIds.has(mappedFinancialType)) {
              return { ...doc, document_type: mappedFinancialType };
            }
          }

          return doc;
        } catch {
          return doc;
        }
      }) ?? [];

    const filteredRemappedData = remappedInitialData.filter((doc) =>
      currentDocIds.has(doc.document_type),
    );

    return {
      filteredRemappedData,
      allRemappedUploadedDocs: remappedInitialData,
    };
  } catch {
    const currentDocIds = new Set<string>();
    fallbackDocs.forEach((doc) => {
      currentDocIds.add(doc.id);
      doc.items?.forEach((subItem) => {
        currentDocIds.add(subItem.id);
      });
    });
    const filteredRemappedData = (uploadedDocs ?? []).filter((doc) =>
      currentDocIds.has(doc.document_type),
    );
    return {
      filteredRemappedData,
      allRemappedUploadedDocs: filteredRemappedData,
    };
  }
};

export const toUploadControllerDictionary = (
  docs: RequiredDoc[],
  options?: UploadControllerDictionaryOptions,
): any[] =>
  docs.map((doc) => {
    const normalizedDoc = ensureTransportProofSubItems(doc);

    return {
      ...normalizedDoc,
      name: toCamelCase(normalizedDoc.display_name),
      label: normalizedDoc.display_name,
      value: normalizedDoc.display_name,
      ...(options?.includeIsRequired !== false
        ? { isRequired: normalizedDoc.is_mandatory !== false }
        : {}),
      items: (normalizedDoc.items ?? []).map((sub) => {
        const enriched = enrichTransportProofChildSlot(sub);
        return {
          ...enriched,
          name: toCamelCase(enriched.display_name),
          label: enriched.display_name,
          value: enriched.display_name,
          ...(options?.includeSubItemYearlySetCount
            ? { yearly_set_count: enriched.yearly_set_count }
            : {}),
          ...(options?.includeIsRequired !== false
            ? { isRequired: normalizedDoc.is_mandatory !== false }
            : {}),
        };
      }),
    };
  }) as any[];

export const buildDocumentTypeCategoryMap = (
  docs: RequiredDoc[],
): Map<string, string> => {
  const categoryMap = new Map<string, string>();

  const walk = (doc: RequiredDoc) => {
    if (doc.id) {
      categoryMap.set(doc.id, doc.display_name);
      categoryMap.set(toAdditionalDocumentTypeCode(doc.id), doc.display_name);
    }
    (doc.items ?? []).forEach(walk);
  };

  docs.forEach(walk);
  return categoryMap;
};

export type GroupedAccountDocuments = {
  category: string;
  documents: CompanyDoc[];
};

export const groupAccountDocsByCategory = (
  accountDocs: CompanyDoc[],
  categoryMap: Map<string, string>,
): GroupedAccountDocuments[] => {
  const groupedDocuments = new Map<string, CompanyDoc[]>();

  accountDocs.forEach((doc) => {
    const category = resolveDocumentCategoryLabel(doc, categoryMap);

    const existingDocs = groupedDocuments.get(category) ?? [];
    existingDocs.push(doc);
    groupedDocuments.set(category, existingDocs);
  });

  return Array.from(groupedDocuments.entries())
    .map(([category, documents]) => ({ category, documents }))
    .sort((leftGroup, rightGroup) =>
      leftGroup.category.localeCompare(rightGroup.category),
    );
};

const isDocComplete = (doc?: RequiredDoc) => {
  if (!doc) return false;
  const requiredCount = doc.required_upload_count ?? doc.yearly_set_count ?? 1;
  const uploadedCount = doc.count_unique_uploaded ?? 0;
  return uploadedCount >= requiredCount;
};

const isAuditedFinancialDoc = (doc: RequiredDoc) =>
  doc.id === AUDITED_FINANCIALS_DOC_TYPE_ID ||
  normalizeDocName(doc.display_name) === "auditedfinancials";

const isManagementFinancialDoc = (doc: RequiredDoc) =>
  doc.id === MANAGEMENT_FINANCIALS_DOC_TYPE_ID ||
  normalizeDocName(doc.display_name) === "managementfinancials";

/**
 * Evaluates mandatory onboarding-document completion from `/documents/list` payload.
 * Supports both shapes:
 * - single `financial-statement` mandatory document (factoring/simple flow)
 * - split reverse-factoring financial documents (`audited` + `management`)
 */
export const areMandatoryRequiredDocsComplete = (
  docs?: RequiredDoc[] | null,
  options?: { account_product?: string | null; companyType?: string | null },
) => {
  const documents = docs ?? [];
  if (!documents.length) return false;

  const mandatoryDocuments = documents.filter((doc) => doc.is_mandatory);
  if (!mandatoryDocuments.length) return false;

  const useSimpleFinancialUpload = shouldUseSimpleFinancialUpload(
    options?.account_product,
    options?.companyType,
  );

  const auditedFinancialDoc = documents.find(isAuditedFinancialDoc);
  const managementFinancialDoc = documents.find(isManagementFinancialDoc);
  const hasSplitFinancialDocs =
    Boolean(auditedFinancialDoc) && Boolean(managementFinancialDoc);
  const areSplitFinancialDocsComplete =
    isDocComplete(auditedFinancialDoc) && isDocComplete(managementFinancialDoc);

  return mandatoryDocuments.every((doc) => {
    const childItems = Array.isArray(doc.items) ? doc.items : [];
    const isParentComplete = isDocComplete(doc);

    if (
      isFinancialStatementDoc(doc) &&
      !useSimpleFinancialUpload &&
      hasSplitFinancialDocs
    ) {
      return isParentComplete || areSplitFinancialDocsComplete;
    }

    if (!childItems.length) {
      return isParentComplete;
    }

    const mandatoryChildren = childItems.filter(
      (childDoc) => childDoc.is_mandatory ?? doc.is_mandatory,
    );
    const areMandatoryChildrenComplete =
      mandatoryChildren.length > 0 &&
      mandatoryChildren.every((childDoc) => isDocComplete(childDoc));

    return isParentComplete || areMandatoryChildrenComplete;
  });
};
